import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { speechToText, stopListening } from '../services/speechToText'
import { textToSpeech } from '../services/textToSpeech'
import { parseVoiceCommand } from '../services/nlpAgent'

interface VoiceState {
  isListening: boolean
  transcript: string
  isProcessing: boolean
  isSpeaking: boolean
  language: string
  supportedLanguages: string[]
  error: string | null
}

interface VoiceContextType extends VoiceState {
  startListening: () => void
  stopListening: () => void
  speak: (text: string, language?: string) => Promise<void>
  processVoiceCommand: (transcript: string) => Promise<any>
  setLanguage: (language: string) => void
  clearError: () => void
  clearErrors: () => void
}

const VoiceContext = createContext<VoiceContextType | undefined>(undefined)

export const useVoice = () => {
  const context = useContext(VoiceContext)
  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider')
  }
  return context
}

interface VoiceProviderProps {
  children: ReactNode
}

export const VoiceProvider: React.FC<VoiceProviderProps> = ({ children }) => {
  const [voiceState, setVoiceState] = useState<VoiceState>({
    isListening: false,
    transcript: '',
    isProcessing: false,
    isSpeaking: false,
    language: 'en-US',
    supportedLanguages: [
      'en-US', 'es-ES', 'fr-FR', 'de-DE', 'it-IT', 'pt-BR',
      'ru-RU', 'ja-JP', 'ko-KR', 'zh-CN', 'ar-SA', 'hi-IN'
    ],
    error: null
  })

  const startListening = () => {
    if (voiceState.isListening) return

    setVoiceState(prev => ({ ...prev, isListening: true, error: null, transcript: '' }))
    
    speechToText(
      voiceState.language,
      (transcript) => {
        setVoiceState(prev => ({ ...prev, transcript }))
      },
      (finalTranscript) => {
        setVoiceState(prev => ({ ...prev, transcript: finalTranscript, isListening: false }))
        if (finalTranscript.trim()) {
          processVoiceCommand(finalTranscript)
        }
      },
      (error) => {
        setVoiceState(prev => ({ 
          ...prev, 
          error: error.message, 
          isListening: false 
        }))
      }
    )
  }

  const stopListeningHandler = () => {
    stopListening()
    setVoiceState(prev => ({ ...prev, isListening: false }))
  }

  const speak = async (text: string, language?: string) => {
    if (voiceState.isSpeaking) return

    setVoiceState(prev => ({ ...prev, isSpeaking: true }))
    
    try {
      await textToSpeech(text, language || voiceState.language)
    } catch (error) {
      console.error('Text-to-speech error:', error)
    } finally {
      setVoiceState(prev => ({ ...prev, isSpeaking: false }))
    }
  }

  const processVoiceCommand = async (transcript: string) => {
    setVoiceState(prev => ({ ...prev, isProcessing: true }))
    
    try {
      const command = await parseVoiceCommand(transcript, voiceState.language)
      return command
    } catch (error) {
      console.error('Voice command processing error:', error)
      setVoiceState(prev => ({ 
        ...prev, 
        error: 'Failed to process voice command' 
      }))
      throw error
    } finally {
      setVoiceState(prev => ({ ...prev, isProcessing: false }))
    }
  }

  const setLanguage = (language: string) => {
    if (voiceState.supportedLanguages.includes(language)) {
      setVoiceState(prev => ({ ...prev, language }))
      localStorage.setItem('voiceLanguage', language)
    }
  }

  const clearError = () => {
    setVoiceState(prev => ({ ...prev, error: null }))
  }

  // Load saved language on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('voiceLanguage')
    if (savedLanguage && voiceState.supportedLanguages.includes(savedLanguage)) {
      setLanguage(savedLanguage)
    }
  }, [])

  // Check for speech recognition support
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setVoiceState(prev => ({ 
        ...prev, 
        error: 'Speech recognition not supported in this browser' 
      }))
    }
  }, [])

  const value: VoiceContextType = {
    ...voiceState,
    startListening,
    stopListening: stopListeningHandler,
    speak,
    processVoiceCommand,
    setLanguage,
    clearError,
    clearErrors: clearError
  }

  return (
    <VoiceContext.Provider value={value}>
      {children}
    </VoiceContext.Provider>
  )
}